/**
 * @file Progressive Image Loader Web Component
 * @description Reusable component for blur-up progressive image loading.
 * Works with both <picture> elements and standalone <img> tags.
 *
 * Features:
 * - Blurred low-res placeholder that loads immediately
 * - Smooth fade-in transition when main image loads
 * - Configurable via data attributes
 * - Automatic cleanup on disconnect
 * - Respects prefers-reduced-motion
 *
 * Data attributes:
 * - data-main-selector: CSS selector for the main image (default: 'img:last-of-type')
 * - data-placeholder-selector: CSS selector for placeholder (default: '.progressive-image__placeholder')
 * - data-transition-duration: Fade duration in ms (default: 400)
 *
 * Required CSS classes added by component:
 * - .progressive-image--loaded: Added to main image when loaded (triggers opacity transition)
 * - .progressive-image-container--loaded: Added to container when image loaded
 *
 * ============================================================================
 * HOW TO APPLY TO OTHER SECTIONS
 * ============================================================================
 *
 * 1. LIQUID TEMPLATE STRUCTURE:
 *
 *    Wrap your image container with the <progressive-image-loader> custom element:
 *
 *    {% if section.settings.enable_progressive_loading %}
 *      <progressive-image-loader
 *        class="your-section__background"
 *        data-main-selector=".your-section__image"
 *        data-placeholder-selector=".your-section__placeholder"
 *        data-transition-duration="400"
 *      >
 *        <!-- Placeholder: tiny image, loads immediately -->
 *        <img
 *          class="your-section__placeholder"
 *          src="{{ image | image_url: width: 40 }}"
 *          alt=""
 *          aria-hidden="true"
 *        >
 *
 *        <!-- Main image: full resolution -->
 *        <picture>
 *          <source media="(max-width: 767px)" srcset="...">
 *          <img class="your-section__image" src="..." loading="lazy">
 *        </picture>
 *      </progressive-image-loader>
 *    {% else %}
 *      <!-- Standard rendering without progressive loading -->
 *      <div class="your-section__background">
 *        <picture>...</picture>
 *      </div>
 *    {% endif %}
 *
 * 2. REQUIRED CSS:
 *
 *    Add these styles to your section's {% style %} block:
 *
 *    .your-section__placeholder {
 *      position: absolute;
 *      inset: 0;
 *      width: 100%;
 *      height: 100%;
 *      object-fit: cover;
 *      filter: blur(10px);
 *      transform: scale(1.1);
 *      z-index: 0;
 *    }
 *
 *    progressive-image-loader .your-section__image {
 *      position: relative;
 *      z-index: 1;
 *      opacity: 0;
 *      transition: opacity 400ms ease-out;
 *    }
 *
 *    progressive-image-loader .your-section__image.progressive-image--loaded {
 *      opacity: 1;
 *    }
 *
 *    @media (prefers-reduced-motion: reduce) {
 *      progressive-image-loader .your-section__image {
 *        transition: none;
 *        opacity: 1;
 *      }
 *      .your-section__placeholder {
 *        display: none;
 *      }
 *    }
 *
 * 3. INCLUDE THE SCRIPT (conditionally):
 *
 *    {% if section.settings.enable_progressive_loading %}
 *      <script src="{{ 'progressive-image-loader.js' | asset_url }}" defer></script>
 *    {% endif %}
 *
 * 4. ADD SCHEMA SETTINGS:
 *
 *    {
 *      "type": "checkbox",
 *      "id": "enable_progressive_loading",
 *      "label": "Enable Progressive Loading",
 *      "default": true,
 *      "info": "Shows a blurred placeholder while the full image loads."
 *    },
 *    {
 *      "type": "select",
 *      "id": "section_position",
 *      "label": "Section Position",
 *      "default": "below_fold",
 *      "options": [
 *        { "value": "above_fold", "label": "Above the Fold" },
 *        { "value": "below_fold", "label": "Below the Fold" }
 *      ],
 *      "info": "Above fold loads eagerly, below fold uses lazy loading."
 *    }
 *
 * ============================================================================
 */

if (!customElements.get('progressive-image-loader')) {
  class ProgressiveImageLoader extends HTMLElement {
    constructor() {
      super();
      this.abortController = new AbortController();
      this.mainImage = null;
      this.placeholder = null;
    }

    connectedCallback() {
      this.init();
    }

    disconnectedCallback() {
      this.abortController.abort();
    }

    /**
     * Initialize progressive image loading
     */
    init() {
      const mainSelector = this.dataset.mainSelector || 'img:last-of-type';
      const placeholderSelector = this.dataset.placeholderSelector || '.progressive-image__placeholder';
      const transitionDuration = parseInt(this.dataset.transitionDuration, 10) || 400;

      // Find main image - could be inside a <picture> or standalone
      this.mainImage = this.querySelector(mainSelector);
      this.placeholder = this.querySelector(placeholderSelector);

      if (!this.mainImage) {
        console.warn('ProgressiveImageLoader: Main image not found with selector:', mainSelector);
        return;
      }

      if (!this.placeholder) {
        console.warn('ProgressiveImageLoader: Placeholder not found with selector:', placeholderSelector);
        return;
      }

      // Set transition duration via CSS custom property
      this.style.setProperty('--progressive-transition-duration', `${transitionDuration}ms`);

      // Check if image is already loaded (cached)
      if (this.mainImage.complete && this.mainImage.naturalWidth > 0) {
        this.handleImageLoaded();
        return;
      }

      // Listen for load event
      this.mainImage.addEventListener('load', () => this.handleImageLoaded(), {
        signal: this.abortController.signal,
        once: true
      });

      // Handle error case - still show image even if load fails
      this.mainImage.addEventListener('error', () => this.handleImageLoaded(), {
        signal: this.abortController.signal,
        once: true
      });
    }

    /**
     * Handle when main image has loaded
     */
    handleImageLoaded() {
      // Add loaded class to trigger fade-in
      this.mainImage.classList.add('progressive-image--loaded');
      this.classList.add('progressive-image-container--loaded');

      // Optionally hide placeholder after transition completes for performance
      const transitionDuration = parseInt(this.dataset.transitionDuration, 10) || 400;
      setTimeout(() => {
        if (this.placeholder) {
          this.placeholder.style.visibility = 'hidden';
        }
      }, transitionDuration + 50);
    }
  }

  customElements.define('progressive-image-loader', ProgressiveImageLoader);
}

